import { linter, type Diagnostic } from "@codemirror/lint";
import { VAR_REF_PATTERN } from "@/components/SpotflowV2/Sidebar/variableRefs";

// Same-length placeholders keep parser error offsets aligned with the real
// document. Warning (not error) severity throughout: runtime interpolation
// can still change validity either way.
function substituteVars(text: string): string {
  const re = new RegExp(VAR_REF_PATTERN.source, "g");
  return text.replace(re, (token) => "0".padEnd(token.length, " "));
}

export function jsonWithVarsLinter() {
  return linter((view): Diagnostic[] => {
    const text = view.state.doc.toString();
    if (!text.trim()) return [];
    const substituted = substituteVars(text);
    // The runtime strips ```json fences too. Stripping shifts offsets, so
    // the diagnostic then covers the whole document.
    const cleaned = substituted.replace(/```json|```/g, "");
    const shifted = cleaned !== substituted;
    try {
      JSON.parse(cleaned);
      return [];
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      let from = 0;
      let to = text.length;
      const positionMatch = /position (\d+)/.exec(message);
      if (!shifted && positionMatch) {
        const pos = Math.min(Number(positionMatch[1]), text.length);
        from = Math.max(0, pos - 1);
        to = Math.min(text.length, pos + 1);
      }
      return [
        { from, to, severity: "warning", message: `Invalid JSON: ${message}` },
      ];
    }
  });
}

export function xmlWithVarsLinter() {
  return linter((view): Diagnostic[] => {
    const text = view.state.doc.toString();
    if (!text.trim()) return [];
    const parsed = new DOMParser().parseFromString(
      substituteVars(text),
      "application/xml"
    );
    const parserError = parsed.querySelector("parsererror");
    if (!parserError) return [];
    const raw = parserError.textContent ?? "";
    // Error text is browser-specific (Chrome: "error on line 2 at column 5",
    // Firefox: "Line Number 2, Column 5") — best-effort, else whole document.
    const detail =
      /error on line \d+ at column \d+:[^\n]*/.exec(raw)?.[0] ??
      raw.split("\n").find((l) => l.trim()) ??
      "malformed document";
    const lineCol = /line (?:number )?(\d+)(?:,| at)? column:? (\d+)/i.exec(
      raw
    );
    let from = 0;
    let to = text.length;
    if (lineCol) {
      const lineNo = Math.min(Number(lineCol[1]), view.state.doc.lines);
      const line = view.state.doc.line(Math.max(1, lineNo));
      from = Math.min(line.from + Number(lineCol[2]) - 1, line.to);
      to = Math.min(from + 1, text.length);
    }
    return [
      {
        from,
        to,
        severity: "warning",
        message: `Invalid XML: ${detail.trim()}`,
      },
    ];
  });
}
